import { useId, useState, type FormEvent } from 'react';
import { ArrowLeft, AtSign, BookOpen, Eye, EyeOff, KeyRound, LockKeyhole, Mail, UserRound } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui';
import { inviteAccessService, type InviteAccessService } from '../../services/inviteAccessService';
import { hashInviteCode, isValidInviteCode, normalizeInviteCode, type InviteCodeHash } from '../../utils/inviteCode';

type AuthMode = 'sign-in' | 'sign-up';
type SignUpStep = 'account' | 'invite';
type View = AuthMode | 'recovery';

const friendlyError = (error: unknown) => error instanceof Error
  ? error.message
  : 'Não foi possível concluir esta ação. Tente novamente.';

const PasswordField = ({
  autoComplete,
  label = 'Senha',
  minLength,
  onChange,
  value
}: {
  autoComplete: 'current-password' | 'new-password';
  label?: string;
  minLength?: number;
  onChange(value: string): void;
  value: string;
}) => {
  const [visible, setVisible] = useState(false);
  const inputId = useId();
  return <div className="auth-field">
    <label htmlFor={inputId}>{label}</label>
    <span className="auth-input-wrap">
      <LockKeyhole aria-hidden="true" />
      <input
        id={inputId}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        minLength={minLength}
        required
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        className="auth-password-toggle"
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={visible}
        onClick={() => setVisible((current) => !current)}
      >{visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}</button>
    </span>
  </div>;
};

export const AuthPage = ({ accessService = inviteAccessService }: { accessService?: InviteAccessService }) => {
  const auth = useAuth();
  const [view, setView] = useState<View>('sign-in');
  const [signUpStep, setSignUpStep] = useState<SignUpStep>('invite');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeHash, setInviteCodeHash] = useState<InviteCodeHash>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();

  const clearFeedback = () => {
    setError(undefined);
    setMessage(undefined);
  };

  const switchView = (nextView: View) => {
    setView(nextView);
    setSignUpStep('invite');
    setPassword('');
    setInviteCode('');
    setInviteCodeHash(undefined);
    clearFeedback();
  };

  const submitSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    clearFeedback();
    try {
      await auth.signIn(email, password);
      setPassword('');
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  };

  const submitRecovery = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    clearFeedback();
    try {
      await auth.requestPasswordReset(email);
      setMessage('Se houver uma conta com este e-mail, você receberá um link seguro para criar uma nova senha.');
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  };

  const submitInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearFeedback();
    const normalizedCode = normalizeInviteCode(inviteCode);
    if (!isValidInviteCode(normalizedCode)) {
      setError('Confira o código de convite e tente novamente.');
      return;
    }

    setBusy(true);
    try {
      const hash = await hashInviteCode(normalizedCode);
      const available = await accessService.isAvailable(hash);
      if (!available) {
        setError('Este convite não está disponível. Confira o código ou tente outro.');
        return;
      }
      setInviteCode('');
      setInviteCodeHash(hash);
      setSignUpStep('account');
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  };

  const submitSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearFeedback();
    const displayName = name.trim();
    if (!displayName) {
      setError('Informe seu nome para criar a conta.');
      return;
    }
    if (!inviteCodeHash) {
      setSignUpStep('invite');
      setError('Verifique seu código de convite novamente.');
      return;
    }

    setBusy(true);
    try {
      const result = await auth.signUp(email, password, inviteCodeHash, displayName);
      setPassword('');
      setInviteCodeHash(undefined);
      if (result.needsEmailConfirmation) {
        setMessage('Enviamos um link de confirmação para seu e-mail. Confirme o endereço antes de entrar no LEITOUR.');
      }
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  };

  const useAnotherInvite = () => {
    setSignUpStep('invite');
    setInviteCodeHash(undefined);
    setInviteCode('');
    setPassword('');
    clearFeedback();
  };

  const isSignUp = view === 'sign-up';

  return <main className="auth-page">
    <div className="auth-page__inner">
      <header className="auth-hero">
        <div className="auth-hero__copy">
          <span className="auth-eyebrow">SEU ESPAÇO DE LEITURA</span>
          <h1>Entrar no LEITOUR</h1>
          <p>Acesse sua conta ou crie uma com código de convite.</p>
        </div>
        <div className="auth-portal" aria-hidden="true">
          <span className="auth-portal__door"><BookOpen /></span>
          <span className="auth-portal__glow" />
        </div>
      </header>

      <section className="auth-card" aria-labelledby="auth-card-title">
        {view !== 'recovery' && <div className="auth-segmented" role="group" aria-label="Escolha como acessar">
          <button type="button" className={!isSignUp ? 'is-active' : ''} aria-pressed={!isSignUp} onClick={() => switchView('sign-in')}>Entrar</button>
          <button type="button" className={isSignUp ? 'is-active' : ''} aria-pressed={isSignUp} onClick={() => switchView('sign-up')}>Criar conta</button>
        </div>}

        {view === 'sign-in' && <form className="auth-page-form" onSubmit={submitSignIn}>
          <div className="auth-form-heading"><h2 id="auth-card-title">Bem-vindo de volta</h2><p>Continue de onde sua leitura parou.</p></div>
          <label className="auth-field">
            <span>E-mail</span>
            <span className="auth-input-wrap"><Mail aria-hidden="true" /><input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={email} onChange={(event) => setEmail(event.target.value)} /></span>
          </label>
          <PasswordField autoComplete="current-password" value={password} onChange={setPassword} />
          {error && <p className="auth-message auth-message--error" role="alert">{error}</p>}
          <Button type="submit" className="button--full auth-cta" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</Button>
          <button type="button" className="auth-link" onClick={() => switchView('recovery')}>Esqueci minha senha</button>
          <p className="auth-switch-copy">Ainda não tem acesso? <button type="button" onClick={() => switchView('sign-up')}>Criar conta</button></p>
        </form>}

        {view === 'recovery' && <form className="auth-page-form" onSubmit={submitRecovery}>
          <button type="button" className="auth-back" onClick={() => switchView('sign-in')}><ArrowLeft aria-hidden="true" /> Voltar</button>
          <div className="auth-form-heading"><h2 id="auth-card-title">Recuperar senha</h2><p>Enviaremos um link seguro para seu e-mail.</p></div>
          <label className="auth-field">
            <span>E-mail</span>
            <span className="auth-input-wrap"><AtSign aria-hidden="true" /><input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" required value={email} onChange={(event) => setEmail(event.target.value)} /></span>
          </label>
          {message && <p className="auth-message auth-message--success" role="status">{message}</p>}
          {error && <p className="auth-message auth-message--error" role="alert">{error}</p>}
          <Button type="submit" className="button--full auth-cta" disabled={busy || Boolean(message)}>{busy ? 'Enviando…' : 'Enviar link seguro'}</Button>
        </form>}

        {view === 'sign-up' && signUpStep === 'invite' && <form className="auth-page-form" onSubmit={submitInvite}>
          <div className="auth-form-heading"><h2 id="auth-card-title">Código de convite</h2><p>Somente usuários com código liberado podem criar conta.</p></div>
          <label className="auth-field auth-field--invite">
            <span>Código de convite <small>Obrigatório</small></span>
            <span className="auth-input-wrap"><KeyRound aria-hidden="true" /><input type="text" autoComplete="off" autoCapitalize="characters" autoCorrect="off" spellCheck={false} placeholder="LEITOUR-XXXX-XXXX-XXXX-XXXX" required disabled={busy} value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} /></span>
          </label>
          {error && <p className="auth-message auth-message--error" role="alert">{error}</p>}
          <Button type="submit" className="button--full auth-cta" disabled={busy}>{busy ? 'Verificando…' : 'Continuar'}</Button>
          <button type="button" className="auth-link" onClick={() => switchView('sign-in')}>Já tenho uma conta</button>
        </form>}

        {view === 'sign-up' && signUpStep === 'account' && <form className="auth-page-form" onSubmit={submitSignUp}>
          <div className="auth-form-heading"><h2 id="auth-card-title">Seus dados</h2><p>O convite foi validado. Agora crie seu acesso.</p></div>
          <label className="auth-field">
            <span>Nome</span>
            <span className="auth-input-wrap"><UserRound aria-hidden="true" /><input type="text" autoComplete="name" required value={name} onChange={(event) => setName(event.target.value)} /></span>
          </label>
          <label className="auth-field">
            <span>E-mail</span>
            <span className="auth-input-wrap"><Mail aria-hidden="true" /><input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={email} onChange={(event) => setEmail(event.target.value)} /></span>
          </label>
          <PasswordField autoComplete="new-password" minLength={8} value={password} onChange={setPassword} />
          <small className="auth-password-hint">Use pelo menos 8 caracteres.</small>
          {message && <p className="auth-message auth-message--success" role="status">{message}</p>}
          {error && <p className="auth-message auth-message--error" role="alert">{error}</p>}
          <Button type="submit" className="button--full auth-cta" disabled={busy || Boolean(message)}>{busy ? 'Criando conta…' : 'Criar conta'}</Button>
          {!message && <button type="button" className="auth-link" onClick={useAnotherInvite}>Usar outro código</button>}
          <button type="button" className="auth-link" onClick={() => switchView('sign-in')}>Já tenho uma conta</button>
        </form>}
      </section>
    </div>
  </main>;
};
